import React, { useState, useEffect } from "react";
import { saveAs } from "file-saver";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
  Alert,
  AlertColor,
  Paper,
  Divider,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Transaction } from "../types/transaction";
import {
  DuplicateActionEnumModel,
  IExtractSettlementDataModel,
} from "../types/types";
import ImportSection from "../ui-components/import-export/ImportSection";
import ExportSection from "../ui-components/import-export/ExportSection";
import ImportDialog from "../ui-components/import-export/ImportDialog";
import PreviewDialog from "../ui-components/import-export/PreviewDialog";
import ContractUploadDialog from "../ui-components/import-export/ContractUploadDialog";
import PreviewSettlementDialog from "../ui-components/import-export/PreviewSettlementDialog";
import { PageLayout } from "../ui-components/PageLayout";
import { useTransactionUpload } from "../hooks/useTransactionUpload";
import { extractSettlmentData } from "../store/modules/document/documentActions";
import {
  insertSettlementData,
  uploadExcelFile,
  exportToExcel,
} from "../store/modules/transactions/transactionActions";
import ImportExcelDialog from "../ui-components/import-export/ImportDialog";
import { RootState } from "../store/reducers";
import { useSelector } from "react-redux";
import { removeCounty } from "../utils/common";

const ImportExport = () => {
  const theme = useTheme();
  const { currentUser: user } = useSelector((state: RootState) => state.user);
  const auth = useSelector((state: RootState) => state.auth);
  const currentYear = new Date().getFullYear().toString();
  const [selectedYear, setSelectedYear] = useState<string>(currentYear);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [openContractDialog, setOpenContractDialog] = useState(false);
  const [openSettlementPreview, setOpenSettlementPreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<Transaction[]>([]);
  const [settlementPreview, setSettlementPreview] =
    useState<IExtractSettlementDataModel>();
  const [duplicateHandling, setDuplicateHandling] =
    useState<DuplicateActionEnumModel>(DuplicateActionEnumModel.AddAsNew);
  const [enablePropMixLookup, setEnablePropMixLookup] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as AlertColor,
  });

  const [isImportingContracts, setIsImportingContracts] = useState(false);

  // Use the custom hook for transaction upload
  const {
    isUploading,
    uploadProgress,
    uploadSummary,
    connectionError,
    handleUpload: uploadTransaction,
    resetStates,
  } = useTransactionUpload({
    accessToken: auth?.accessToken,
    apiUrl: window.__config.API_URL,
  });

  // Effect to show snackbar when connection error occurs
  useEffect(() => {
    if (connectionError) {
      setSnackbar({
        open: true,
        message: connectionError,
        severity: "error",
      });
    }
  }, [connectionError]);

  const handleYearChange = (event: SelectChangeEvent) => {
    setSelectedYear(event.target.value);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleDuplicateHandlingChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDuplicateHandling(
      event.target.value as unknown as DuplicateActionEnumModel
    );
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: "No file selected. Please select an Excel file to upload.",
        severity: "error",
      });
      return;
    }

    // Use the custom hook's upload function with PropMix option
    await uploadTransaction(
      selectedFile,
      duplicateHandling!,
      enablePropMixLookup
    );
  };

  const handlePropMixLookupChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEnablePropMixLookup(event.target.checked);
  };

  const handleImport = () => {
    setOpenPreviewDialog(false);

    // Simulate final import
    setTimeout(() => {
      // toast({
      //   title: "Import successful",
      //   description: `Imported ${previewData.length} transactions for ${selectedYear}`,
      // });
      setPreviewData([]);
    }, 500);
  };

  function handleImportExcelDialogClose(): void {
    setOpenImportDialog(false);
    setSelectedFile(null);
    resetStates();
    setDuplicateHandling(DuplicateActionEnumModel.AddAsNew);
    setEnablePropMixLookup(false);
  }

  return (
    <PageLayout>
      <Box
        sx={{
          mb: 6,
          ml: 1,
          // ml: isMobile ? 0 : 1
          // px: { xs: 1, sm: 2 },
          // py: 2,
        }}
        component={Paper}
        elevation={0}
        aria-labelledby="page-title"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            color="primary"
            fontWeight="bold"
            id="page-title"
          >
            Import / Export
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={4} sx={{ minHeight: "400px" }}>
          <Grid item xs={12} md={12}>
            <Box sx={{ height: "100%" }}>
              <ImportSection
                onImportClick={() => setOpenImportDialog(true)}
                onContractImportClick={() => setOpenContractDialog(true)}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <Box sx={{ height: "100%" }}>
              <ExportSection
                onExportComplete={() => {
                  setSnackbar({
                    open: true,
                    message: "Transactions exported to Excel successfully",
                    severity: "success",
                  });
                }}
                onExportError={() => {
                  setSnackbar({
                    open: true,
                    message: "Failed to export transactions",
                    severity: "error",
                  });
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Import Dialog */}
        <ImportExcelDialog
          open={openImportDialog}
          onClose={handleImportExcelDialogClose}
          selectedFile={selectedFile}
          isUploading={isUploading}
          handleFileChange={handleFileChange}
          handleUpload={handleUpload}
          uploadProgress={uploadProgress}
          uploadSummary={uploadSummary}
          connectionError={connectionError}
          duplicateHandling={duplicateHandling}
          onDuplicateHandlingChange={handleDuplicateHandlingChange}
          isAdmin={user?.isAdmin ?? false}
          enablePropMixLookup={enablePropMixLookup}
          onPropMixLookupChange={handlePropMixLookupChange}
          aria-labelledby="import-dialog-title"
        />

        {/* Preview Dialog */}
        <PreviewDialog
          open={openPreviewDialog}
          onClose={() => setOpenPreviewDialog(false)}
          previewData={previewData}
          selectedYear={selectedYear}
          handleImport={handleImport}
          aria-labelledby="preview-dialog-title"
        />

        <ContractUploadDialog
          open={openContractDialog}
          onClose={() => setOpenContractDialog(false)}
          onPreview={async (files) => {
            try {
              setIsImportingContracts(true);
              setSnackbar({
                open: true,
                message: "Processing contract files...",
                severity: "info",
              });
              const result = await extractSettlmentData(files);
              setSettlementPreview({
                ...result,
                data: result.data?.map((item) => ({
                  ...item,
                  propertyAddress: item.propertyAddress
                    ? removeCounty(item.propertyAddress)
                    : undefined,
                })),
              });

              setOpenContractDialog(false);
              setOpenSettlementPreview(true);
            } catch (error) {
              setSnackbar({
                open: true,
                message: "Failed to process contract files. Please try again.",
                severity: "error",
              });
            } finally {
              setIsImportingContracts(false);
            }
          }}
          isUploading={isImportingContracts}
          aria-labelledby="contract-upload-dialog-title"
        />
        <PreviewSettlementDialog
          open={openSettlementPreview}
          onClose={() => {
            setOpenSettlementPreview(false);
            //clear selected file
            setSelectedFile(null);
            setSettlementPreview(undefined);
          }}
          onImport={async (data, action) => {
            try {
              setSnackbar({
                open: true,
                message: "Importing settlement data...",
                severity: "info",
              });
              const affectedRows = await insertSettlementData(data, action);
              setOpenSettlementPreview(false);
              // Reset duplicate handling after successful import
              setDuplicateHandling(DuplicateActionEnumModel.AddAsNew);
              setSnackbar({
                open: true,
                message: `Successfully imported ${affectedRows} settlement records`,
                severity: "success",
              });
            } catch (error) {
              setSnackbar({
                open: true,
                message: "Failed to import settlement data. Please try again.",
                severity: "error",
              });
            }
          }}
          previewData={settlementPreview}
          isUploading={isUploading}
          aria-labelledby="settlement-preview-dialog-title"
        />
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
          elevation={6}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default ImportExport;
